/**
 * AdoraCare - the intake drawer (PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-
 * CHECKIN-SPINE-V1; RESHAPED by AUG-12-WALK-2-REGISTRATION-TRUTH-V1 F3).
 *
 * SINCE THE F3 RESHAPE this drawer serves ONLY sessions already in flight:
 *   - paper in_progress -> the typed review + "Confirm and check in"
 *   - completed -> the read-only review
 *   - a completion's identity-review / identity-conflict states
 * Fresh starts open the Complete Registration MODAL instead
 * (CompleteRegistrationModal.tsx); digital in-progress sessions resume the
 * ONE form (/patient-intake/:id) directly. The retired choice options
 * (On this tablet / On paper / Complete digitally), the check-in-without-
 * intake bypass, and the Not-now footer are GONE (founder walk W-6/W-7);
 * "Cancel intake" survives as the lawful destructive stop.
 *
 * DOCUMENT HONESTY: no binary-document owner exists yet (V6-P2 is
 * design-only) -- the paper path states plainly that the original form
 * stays filed at the desk and no digital copy is stored. OCR is absent by
 * design (INTAKE-OCR-EXTRACTION = registered FUTURE work; draft-only law).
 *
 * PRIVACY: form fields flow ONE WAY into the orchestration; no stored
 * contact value is ever fetched or rendered here; candidate cards are the
 * patient-search grade (name/UMR/age/gender/city -- no phone).
 *
 * Tablet posture: large labeled fields, one section at a time, visible
 * focus, errors beside fields + a summary, submit disabled while
 * processing (duplicate-submit protection on top of the server-side
 * requestId idempotency key, which is MINTED ONCE per drawer and HELD
 * across retries).
 */

import { useMemo, useState } from 'react';

import {
  cancelIntakeSession,
  completeIntakeSession,
  type CompleteIntakeResult,
  type IntakeCompleteFields,
} from '@/dashboard/api';
import {
  startIntakeSession,
  type StartIntakeResult,
} from '@/dashboard/appointments/intake-start-api';
import { AcBadge, AcButton, DrawerShell, FieldGrid } from '@/dashboard/ui';
import {
  ConsentPhotoCaptureSlot,
  PENDING_PHOTO_LINE_PAPER_FORM,
  PendingPhotoBlock,
} from '@/dashboard/patients/consent-photo';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { needsLineFromServer } from '@/dashboard/appointments/registration-field-plan';
import { buildAgeView } from '@/lib/age';
import {
  CONSENT_CHANNEL_OPTIONS,
  GENDER_OPTIONS,
  RELATIONSHIP_OPTIONS,
} from '@/dashboard/patients/patient-master-view';

// --- Copy (exported for the deterministic render tests) ---------------------

/** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3; founder walk W-6/W-7): the
 *  choice panel is RETIRED from this drawer. Fresh starts open the Complete
 *  Registration MODAL (CompleteRegistrationModal.tsx -- one question:
 *  self-fill vs we-help); the retired options (On this tablet / On paper /
 *  Complete digitally / Check in without intake / Not now) and the
 *  channel-seam text block are GONE with it. This drawer now serves ONLY
 *  sessions already in flight: paper review, completed review, and the
 *  identity/conflict states of a completion. */
export const INTAKE_STALE_CHOICE_COPY =
  'Use Complete registration on the row to start.';
export const INTAKE_CANCEL_COPY = 'Cancel intake';
export const INTAKE_PAPER_HANDED_COPY =
  'Hand the paper form to the patient. When it comes back, review it here.';
export const INTAKE_PAPER_RETURNED_COPY = 'Form returned - review now';
export const INTAKE_PAPER_DOCUMENT_HONESTY_COPY =
  'The original form stays filed at the desk. A digital copy is not stored yet - document storage is planned work.';
export const INTAKE_DIGITAL_REVIEW_COPY = 'Review details';
export const INTAKE_DIGITAL_SUBMIT_COPY = 'Submit intake';
export const INTAKE_PAPER_CONFIRM_COPY = 'Confirm and check in';
export const INTAKE_DONE_TITLE_COPY = 'Checked in - waiting for doctor';
export const INTAKE_DONE_PARTIAL_TITLE_COPY = 'Intake complete - check-in pending';
export const INTAKE_REVIEW_TITLE_COPY = 'Possible existing patient - review';
export const INTAKE_REVIEW_USE_COPY = 'Use this patient';
export const INTAKE_REVIEW_CREATE_COPY = 'None of these - create new patient';
export const INTAKE_CONFLICT_TITLE_COPY =
  'Identity conflict - review required';
export const INTAKE_CONFLICT_BODY_COPY =
  'This appointment already references a different patient record. Check-in was NOT performed and nothing was overwritten. Review the identity on the lead record before continuing.';
/** WALK-17-verifiable labels for the DIRECT appointment->patient link
 * (founder corrective ruling 2026-07-14). */
export const DIRECT_LINK_LABELS: Readonly<Record<string, string>> = {
  linked: 'Linked to this patient',
  already_linked: 'Already linked to this patient',
  conflict: 'Conflict - references a different patient',
  failed: 'Link failed - retry the completion',
};
export const INTAKE_MINOR_NOTE_COPY =
  'Under 18: a guardian and recorded consent are required.';
export const INTAKE_CONSENT_LABEL_COPY =
  'The patient agrees their details are used for care and administration.';

// --- The identity door copy (IDENTITY-LINK-AT-INTAKE-V1; plain front-desk
// language per the checkpoint idiom -- exported for pinning) ----------------

export const DOOR_TITLE_COPY =
  'This phone number is already used by these patients. Who is this visit for?';
export const DOOR_SELECT_COPY = 'This is the patient';
export const DOOR_CREATE_COPY = 'Different family member / new patient';
export const DOOR_BOOKED_HEADING_COPY = 'Already booked on this number';
export const DOOR_LAST_VISIT_LABEL = 'Last visit';
export const DOOR_NO_VISIT_COPY = 'No past visit recorded';
export const DOOR_LINKED_TITLE_COPY = 'Visit linked to the patient record';
export const DOOR_LINKED_BODY_COPY =
  'This patient is already registered - no intake form is needed. Close to continue from the board.';
export const COMPLETED_REVIEW_TITLE_COPY = 'Intake already completed';
export const COMPLETED_REVIEW_BODY_COPY =
  'This visit already has a completed intake. The details live on the patient record - nothing needs to be filled in again.';

// --- Pure helpers (exported for tests) --------------------------------------

export type IntakeDrawerView =
  | 'choice'
  | 'digital_form'
  | 'digital_review'
  | 'paper_pending'
  | 'paper_review'
  | 'identity_review'
  | 'identity_conflict'
  | 'completed_review'
  | 'done';

/** Where the drawer opens, from the row's intake facts. */
export function deriveInitialView(
  intakeStatus: 'none' | 'in_progress' | 'completed' | undefined,
  intakeMode: 'digital' | 'paper' | null | undefined,
): IntakeDrawerView {
  if (intakeStatus === 'in_progress') {
    return intakeMode === 'paper' ? 'paper_review' : 'digital_form';
  }
  if (intakeStatus === 'completed') {
    // T9a: a completed intake opens as a read-only review, never as a
    // second start (the row's verb says "Review intake" -- one truth).
    return 'completed_review';
  }
  return 'choice';
}

export interface IntakeFormState {
  fullName: string;
  mobile: string;
  gender: string;
  dob: string;
  ageYears: string;
  city: string;
  guardianName: string;
  guardianRelationship: string;
  consentGrantedBy: string;
  consentChannel: string;
  dataProcessingAgreed: boolean;
}

export const EMPTY_INTAKE_FORM: IntakeFormState = {
  fullName: '',
  mobile: '',
  gender: '',
  dob: '',
  ageYears: '',
  city: '',
  guardianName: '',
  guardianRelationship: 'guardian',
  consentGrantedBy: '',
  consentChannel: 'in_person',
  dataProcessingAgreed: false,
};

/** Age resolution for the minor rule (dob wins; else stated age). Delegates to
 *  THE ONE shared age utility (F-5) so intake age derivation is consistent
 *  with the patient-mode + backend prefill (clinic-local; the drawer has no
 *  clinic timezone in hand, so the tenant default zone is used). */
export function intakeFormAge(form: IntakeFormState): number | null {
  const view = buildAgeView(
    form.dob.trim() !== '' ? form.dob.trim() : null,
    form.ageYears.trim() !== '' ? Number(form.ageYears) : null,
    Date.now(),
    null,
  );
  return view.ageYears;
}

export function intakeFormIsMinor(form: IntakeFormState): boolean {
  const age = intakeFormAge(form);
  return age !== null && age < 18;
}

/** Field-level validation (the server re-validates everything; this is the
 *  beside-the-field honesty layer). Returns label->message, empty = valid. */
export function validateIntakeForm(
  form: IntakeFormState,
): ReadonlyArray<{ field: string; message: string }> {
  const errors: Array<{ field: string; message: string }> = [];
  if (form.fullName.trim() === '') {
    errors.push({ field: 'fullName', message: 'Full name is required.' });
  }
  if (form.mobile.trim() === '') {
    errors.push({ field: 'mobile', message: 'Mobile number is required.' });
  } else if (!/^[+\d][\d\s-]{7,}$/.test(form.mobile.trim())) {
    errors.push({ field: 'mobile', message: 'Enter a valid mobile number.' });
  }
  if (form.gender.trim() === '') {
    errors.push({ field: 'gender', message: 'Pick a gender.' });
  }
  if (form.dob.trim() === '' && form.ageYears.trim() === '') {
    errors.push({ field: 'ageYears', message: 'Date of birth or age is required.' });
  } else if (intakeFormAge(form) === null) {
    errors.push({ field: 'ageYears', message: 'Enter a valid date of birth or age.' });
  }
  if (intakeFormIsMinor(form)) {
    if (form.guardianName.trim() === '') {
      errors.push({ field: 'guardianName', message: 'A minor needs a guardian name.' });
    }
    if (form.consentGrantedBy.trim() === '') {
      errors.push({
        field: 'consentGrantedBy',
        message: 'A minor needs recorded consent (who granted it).',
      });
    }
  }
  if (!form.dataProcessingAgreed) {
    errors.push({
      field: 'dataProcessingAgreed',
      message: 'Consent to data use is required to register.',
    });
  }
  return errors;
}

/** The one-way wire shape (form -> orchestration). */
export function buildCompleteFields(
  form: IntakeFormState,
  mode: 'digital' | 'paper',
): IntakeCompleteFields {
  const channel = mode === 'digital' ? 'form' : 'in_person';
  return {
    fullName: form.fullName.trim(),
    mobile: form.mobile.trim(),
    gender: form.gender,
    ...(form.dob.trim() !== '' ? { dob: form.dob.trim() } : {}),
    ...(form.dob.trim() === '' && form.ageYears.trim() !== ''
      ? { ageYears: Number(form.ageYears) }
      : {}),
    ...(form.city.trim() !== '' ? { city: form.city.trim() } : {}),
    ...(intakeFormIsMinor(form)
      ? {
          guardian: {
            name: form.guardianName.trim(),
            relationship: form.guardianRelationship,
          },
          consent: { grantedBy: form.consentGrantedBy.trim(), channel },
        }
      : {}),
    ...(form.dataProcessingAgreed
      ? {
          dataProcessingConsent: {
            grantedBy: intakeFormIsMinor(form)
              ? form.guardianName.trim() || form.fullName.trim()
              : form.fullName.trim(),
            channel,
          },
        }
      : {}),
  };
}

// --- The identity-door views (pure, SSR-testable seams) ----------------------

export interface IntakeDoorSelectionViewProps {
  readonly showLastVisit?: boolean;
  readonly door: Pick<
    StartIntakeResult,
    'candidates' | 'bookedAppointments' | 'reconciliationPending' | 'warnings'
  >;
  readonly busy: boolean;
  readonly onPick: (patientId: string) => void;
  readonly onCreateNew: () => void;
}

/** THE SELECTION STEP (IDENTITY-LINK-AT-INTAKE-V1): every patient on the
 * number, listed with durable facts (name + UMR + age/gender + last visit);
 * the desk picks explicitly. "Different family member / new patient" is the
 * ONLY road to creation and hides while records are under duplicate review
 * (the server refusal is the law; this is the client posture). */
export function IntakeDoorSelectionView({
  showLastVisit = true,
  door,
  busy,
  onPick,
  onCreateNew,
}: IntakeDoorSelectionViewProps): JSX.Element {
  return (
    <div data-testid="intake-person-selection">
      <p className="intake-drawer__door-question">{DOOR_TITLE_COPY}</p>
      {door.warnings.map((w) => (
        <p key={w} className="intake-drawer__warning" role="status">
          {w}
        </p>
      ))}
      <ul className="intake-drawer__candidates">
        {door.candidates.map((c) => (
          <li key={c.patientId} className="intake-drawer__candidate">
            <span className="intake-drawer__candidate-facts">
              <strong>{c.fullName}</strong>{' '}
              <AcBadge tone="neutral">{c.umr}</AcBadge>
              {c.ageYears !== null ? ` ${c.ageYears}y` : ''}
              {c.gender ? ` ${c.gender}` : ''}
              {showLastVisit ? <span className="intake-drawer__candidate-visit">
                {c.lastVisitAt !== null
                  ? ` ${DOOR_LAST_VISIT_LABEL}: ${formatTimestampIst(c.lastVisitAt)}`
                  : ` ${DOOR_NO_VISIT_COPY}`}
              </span> : null}
            </span>
            <AcButton
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() => onPick(c.patientId)}
              data-testid={`intake-door-link-${c.patientId}`}
            >
              {DOOR_SELECT_COPY}
            </AcButton>
          </li>
        ))}
      </ul>
      {door.bookedAppointments.length > 0 ? (
        <div className="intake-drawer__door-booked" data-testid="intake-door-booked">
          <p className="intake-drawer__door-booked-heading">
            {DOOR_BOOKED_HEADING_COPY}
          </p>
          <ul>
            {door.bookedAppointments.map((b) => (
              <li key={b.appointmentId}>
                <strong>{b.patientName ?? 'Booked visit'}</strong>
                {b.patientUmr ? (
                  <>
                    {' '}
                    <AcBadge tone="neutral">{b.patientUmr}</AcBadge>
                  </>
                ) : null}{' '}
                {formatTimestampIst(b.appointmentTime)} ({b.status})
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {door.reconciliationPending !== true ? (
        <AcButton
          variant="secondary"
          disabled={busy}
          onClick={onCreateNew}
          data-testid="intake-door-create-new"
        >
          {DOOR_CREATE_COPY}
        </AcButton>
      ) : null}
    </div>
  );
}

/** The door's linked outcome: identity resolved in place; no intake needed. */
export function IntakeDoorLinkedView({
  appointmentId,
  patientUmr,
  onClose,
}: {
  readonly appointmentId: number;
  readonly patientUmr: string | null;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div data-testid="intake-door-linked">
      <h3 className="intake-drawer__done-title">{DOOR_LINKED_TITLE_COPY}</h3>
      <p>{DOOR_LINKED_BODY_COPY}</p>
      <FieldGrid
        columns={2}
        fields={[
          { label: 'Patient ID (UMR)', value: patientUmr },
          { label: 'Appointment', value: `#${appointmentId}` },
        ]}
        testId="intake-door-linked-grid"
      />
      <AcButton
        variant="primary"
        onClick={onClose}
        data-testid="intake-door-linked-close"
      >
        Done
      </AcButton>
    </div>
  );
}

// --- The drawer --------------------------------------------------------------

export interface IntakeDrawerApi {
  start: typeof startIntakeSession;
  complete: typeof completeIntakeSession;
  cancel: typeof cancelIntakeSession;
}

const DEFAULT_API: IntakeDrawerApi = {
  start: startIntakeSession,
  complete: completeIntakeSession,
  cancel: cancelIntakeSession,
};

export interface IntakeDrawerProps {
  readonly appointmentId: number;
  /** The row's stored status ('arrived' | 'checked_in') -- display context
   *  only since the F3 reshape retired the skip door. */
  readonly appointmentStatus: string;
  readonly patientLabel: string;
  readonly intakeStatus?: 'none' | 'in_progress' | 'completed';
  readonly intakeMode?: 'digital' | 'paper' | null;
  readonly onClose: () => void;
  /** Refetch the day read (fired after any server-state change). */
  readonly onChanged: () => void;
  /** Fired ONCE when the orchestration completes (outcome != review) --
   *  hosts that track identity separately (the one-motion done panel)
   *  flip their linked state here. */
  readonly onCompleted?: (result: CompleteIntakeResult) => void;
  readonly api?: IntakeDrawerApi;
}

function mintRequestId(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `intake-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export default function IntakeDrawer({
  appointmentId,
  patientLabel,
  intakeStatus,
  intakeMode,
  onClose,
  onChanged,
  onCompleted,
  api = DEFAULT_API,
}: IntakeDrawerProps): JSX.Element {
  const [view, setView] = useState<IntakeDrawerView>(() =>
    deriveInitialView(intakeStatus, intakeMode),
  );
  // Mode is fixed by the SESSION being serviced (fresh starts live in the
  // modal since F3), so it derives once from the row's intake facts.
  const [mode] = useState<'digital' | 'paper'>(
    intakeMode === 'paper' ? 'paper' : 'digital',
  );
  const [form, setForm] = useState<IntakeFormState>(EMPTY_INTAKE_FORM);
  const [errors, setErrors] = useState<ReadonlyArray<{ field: string; message: string }>>(
    [],
  );
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<CompleteIntakeResult | null>(null);
  // (The identity door states retired with the choice panel -- F3: the
  // modal owns the pre-form person pick; this drawer's identity_review view
  // still serves a COMPLETION's candidate state via submitCompletion.)
  // C14: the paper artifact's capture-seam slot (honest later-work marker).
  const [photoSlotOpen, setPhotoSlotOpen] = useState(false);
  // The completion idempotency key: minted ONCE per drawer, HELD across
  // retries and the identity-review round-trip (a replay returns the
  // recorded outcome; a double click cannot double-write).
  const requestId = useMemo(() => mintRequestId(), []);

  const errorFor = (field: string): string | null =>
    errors.find((e) => e.field === field)?.message ?? null;

  // (startSession + the post-door continuation retired with the choice
  // panel -- AUG-12-WALK-2-REGISTRATION-TRUTH-V1 F3: fresh starts live in
  // CompleteRegistrationModal; this drawer only services sessions already
  // in flight.)

  const submitCompletion = (
    patientChoice?: { action: 'link'; patientId: string } | { action: 'create_new' },
  ): void => {
    setBusy(true);
    setServerError(null);
    api
      .complete(appointmentId, {
        requestId,
        fields: buildCompleteFields(form, mode),
        // An explicit person pick / create-new confirm from the
        // identity-review state rides the completion; the server re-checks
        // fail-closed (reconciliation fence + the one writer's own dedupe).
        ...(patientChoice ? { patientChoice } : {}),
      })
      .then((res) => {
        setResult(res);
        if (res.outcome === 'identity_review_required') {
          setView('identity_review');
          return;
        }
        if (res.outcome === 'appointment_identity_conflict') {
          // Founder corrective ruling: a conflicting direct reference is
          // NEVER overwritten -- check-in stopped; human review is the one
          // next action. Server state unchanged, so refetch is honest.
          setView('identity_conflict');
          onChanged();
          return;
        }
        if (res.outcome === 'registration_capture_required') {
          // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect C / no false success):
          // this paper-intake drawer renders only the core identity, NOT the
          // full governed contract (the Part C/D set is owned once by
          // RegistrationContractSections -- never re-rendered here, the drift
          // guard). So a tenant that requires that set cannot be completed from
          // this surface: the server refused. Surface the refusal HONESTLY (the
          // compact needs-line) and STAY on this view -- the previous code fell
          // through to setView('done'), reporting a silent false success on a
          // completion the server rejected. Finishing the contract is the
          // governed registration flow's job (the digital "Complete
          // registration" surface); nothing was captured here.
          setServerError(
            needsLineFromServer(res.missingRegistrationFields ?? []) ??
              'Registration is not complete yet. Complete it on the registration form.',
          );
          onChanged();
          return;
        }
        setView('done');
        onChanged();
        if (onCompleted) onCompleted(res);
      })
      .catch((err: unknown) => {
        setServerError(
          err instanceof Error ? err.message : 'The intake could not be completed.',
        );
      })
      .finally(() => setBusy(false));
  };

  const validateThen = (next: () => void): void => {
    const found = validateIntakeForm(form);
    setErrors(found);
    if (found.length === 0) next();
  };

  const cancelSession = (): void => {
    setBusy(true);
    setServerError(null);
    api
      .cancel(appointmentId)
      .then(() => {
        onChanged();
        onClose();
      })
      .catch(() => {
        // Cancel is a safe stop; closing anyway leaves the session resumable.
        onClose();
      })
      .finally(() => setBusy(false));
  };

  const isMinor = intakeFormIsMinor(form);

  const formFields = (submitCopy: string, onSubmit: () => void): JSX.Element => (
    <div className="intake-drawer__form" data-testid="intake-form">
      {errors.length > 0 ? (
        <div className="intake-drawer__error-summary" role="alert">
          <p>Fix the following before continuing:</p>
          <ul>
            {errors.map((e) => (
              <li key={e.field}>{e.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <label className="intake-drawer__field" htmlFor="intake-full-name">
        <span>Full name (required)</span>
        <input
          id="intake-full-name"
          type="text"
          value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          data-testid="intake-full-name"
          disabled={busy}
        />
        {errorFor('fullName') ? (
          <span className="intake-drawer__field-error">{errorFor('fullName')}</span>
        ) : null}
      </label>
      <label className="intake-drawer__field" htmlFor="intake-mobile">
        <span>Mobile number (required)</span>
        <input
          id="intake-mobile"
          type="tel"
          inputMode="tel"
          value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          data-testid="intake-mobile"
          disabled={busy}
        />
        {errorFor('mobile') ? (
          <span className="intake-drawer__field-error">{errorFor('mobile')}</span>
        ) : null}
      </label>
      <label className="intake-drawer__field" htmlFor="intake-gender">
        <span>Gender (required)</span>
        <select
          id="intake-gender"
          value={form.gender}
          onChange={(e) => setForm({ ...form, gender: e.target.value })}
          data-testid="intake-gender"
          disabled={busy}
        >
          <option value="">Select...</option>
          {GENDER_OPTIONS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
        {errorFor('gender') ? (
          <span className="intake-drawer__field-error">{errorFor('gender')}</span>
        ) : null}
      </label>
      {/* F9 (FOUNDER-WALK-2026-07-31, the R-41 date-label family): the label
          no longer promises a display format -- a native date input renders
          the BROWSER-locale format, so any parenthetical hint here lies to
          someone. One date-input pattern: plain label, the picker speaks its
          own format. The Age-when-DOB-unknown affordance below is untouched
          (the founder called it good). */}
      <label className="intake-drawer__field" htmlFor="intake-dob">
        <span>Date of birth</span>
        <input
          id="intake-dob"
          type="date"
          value={form.dob}
          onChange={(e) => setForm({ ...form, dob: e.target.value })}
          data-testid="intake-dob"
          disabled={busy}
        />
      </label>
      <label className="intake-drawer__field" htmlFor="intake-age">
        <span>Age (years, when date of birth is unknown)</span>
        <input
          id="intake-age"
          type="number"
          min={0}
          max={150}
          value={form.ageYears}
          onChange={(e) => setForm({ ...form, ageYears: e.target.value })}
          data-testid="intake-age"
          disabled={busy}
        />
        {errorFor('ageYears') ? (
          <span className="intake-drawer__field-error">{errorFor('ageYears')}</span>
        ) : null}
      </label>
      <label className="intake-drawer__field" htmlFor="intake-city">
        <span>City (optional)</span>
        <input
          id="intake-city"
          type="text"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          data-testid="intake-city"
          disabled={busy}
        />
      </label>
      {isMinor ? (
        <div className="intake-drawer__minor" data-testid="intake-minor-block">
          <p className="intake-drawer__minor-note">{INTAKE_MINOR_NOTE_COPY}</p>
          <label className="intake-drawer__field" htmlFor="intake-guardian-name">
            <span>Guardian name (required for a minor)</span>
            <input
              id="intake-guardian-name"
              type="text"
              value={form.guardianName}
              onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
              data-testid="intake-guardian-name"
              disabled={busy}
            />
            {errorFor('guardianName') ? (
              <span className="intake-drawer__field-error">
                {errorFor('guardianName')}
              </span>
            ) : null}
          </label>
          <label className="intake-drawer__field" htmlFor="intake-guardian-relationship">
            <span>Guardian relationship</span>
            <select
              id="intake-guardian-relationship"
              value={form.guardianRelationship}
              onChange={(e) =>
                setForm({ ...form, guardianRelationship: e.target.value })
              }
              data-testid="intake-guardian-relationship"
              disabled={busy}
            >
              {RELATIONSHIP_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="intake-drawer__field" htmlFor="intake-consent-granted-by">
            <span>Consent granted by (required for a minor)</span>
            <input
              id="intake-consent-granted-by"
              type="text"
              value={form.consentGrantedBy}
              onChange={(e) => setForm({ ...form, consentGrantedBy: e.target.value })}
              data-testid="intake-consent-granted-by"
              disabled={busy}
            />
            {errorFor('consentGrantedBy') ? (
              <span className="intake-drawer__field-error">
                {errorFor('consentGrantedBy')}
              </span>
            ) : null}
          </label>
          <label className="intake-drawer__field" htmlFor="intake-consent-channel">
            <span>Consent channel</span>
            <select
              id="intake-consent-channel"
              value={form.consentChannel}
              onChange={(e) => setForm({ ...form, consentChannel: e.target.value })}
              data-testid="intake-consent-channel"
              disabled={busy}
            >
              {CONSENT_CHANNEL_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      <label
        className="intake-drawer__field intake-drawer__field--consent"
        htmlFor="intake-data-consent"
      >
        <input
          id="intake-data-consent"
          type="checkbox"
          checked={form.dataProcessingAgreed}
          onChange={(e) => setForm({ ...form, dataProcessingAgreed: e.target.checked })}
          data-testid="intake-data-consent"
          disabled={busy}
        />
        <span>{INTAKE_CONSENT_LABEL_COPY}</span>
        {errorFor('dataProcessingAgreed') ? (
          <span className="intake-drawer__field-error">
            {errorFor('dataProcessingAgreed')}
          </span>
        ) : null}
      </label>
      <AcButton
        variant="primary"
        disabled={busy}
        onClick={onSubmit}
        data-testid="intake-form-submit"
      >
        {submitCopy}
      </AcButton>
    </div>
  );

  const reviewSummary = (): JSX.Element => (
    <FieldGrid
      columns={2}
      fields={[
        { label: 'Full name', value: form.fullName.trim() || null },
        { label: 'Mobile', value: form.mobile.trim() || null },
        {
          label: 'Gender',
          value:
            GENDER_OPTIONS.find((g) => g.value === form.gender)?.label ?? null,
        },
        {
          label: 'Date of birth / age',
          value:
            form.dob.trim() !== ''
              ? form.dob.trim()
              : form.ageYears.trim() !== ''
                ? `${form.ageYears.trim()} years`
                : null,
        },
        { label: 'City', value: form.city.trim() || null },
        ...(isMinor
          ? [
              { label: 'Guardian', value: form.guardianName.trim() || null },
              { label: 'Consent by', value: form.consentGrantedBy.trim() || null },
            ]
          : []),
      ]}
      testId="intake-review-grid"
    />
  );

  const body = (): JSX.Element => {
    switch (view) {
      case 'choice':
        // RESHAPED (F3): fresh starts never open this drawer any more --
        // the board routes them to the Complete Registration modal. A stale
        // open renders the honest pointer, never dead choice buttons.
        return (
          <div className="intake-drawer__choice" data-testid="intake-choice">
            <p>{INTAKE_STALE_CHOICE_COPY}</p>
          </div>
        );
      case 'completed_review':
        return (
          <div data-testid="intake-completed-review">
            <h3 className="intake-drawer__done-title">
              {COMPLETED_REVIEW_TITLE_COPY}
            </h3>
            <p>{COMPLETED_REVIEW_BODY_COPY}</p>
            <FieldGrid
              columns={2}
              fields={[
                { label: 'Patient', value: patientLabel },
                { label: 'Appointment', value: `#${appointmentId}` },
                {
                  label: 'Intake',
                  value: intakeMode === 'paper' ? 'Completed - paper form' : 'Completed - digital',
                },
              ]}
              testId="intake-completed-review-grid"
            />
            <AcButton
              variant="primary"
              onClick={onClose}
              data-testid="intake-completed-review-close"
            >
              Done
            </AcButton>
          </div>
        );
      case 'digital_form':
        return (
          <div data-testid="intake-digital">
            <p className="intake-drawer__handoff">
              Hand the device to the patient to fill their details; they review
              before submitting.
            </p>
            {formFields(INTAKE_DIGITAL_REVIEW_COPY, () =>
              validateThen(() => setView('digital_review')),
            )}
          </div>
        );
      case 'digital_review':
        return (
          <div data-testid="intake-digital-review">
            <p>Review the details, then submit. Check-in completes automatically.</p>
            {reviewSummary()}
            <div className="intake-drawer__review-actions">
              <AcButton
                variant="primary"
                disabled={busy}
                onClick={() => submitCompletion()}
                data-testid="intake-submit"
              >
                {INTAKE_DIGITAL_SUBMIT_COPY}
              </AcButton>
              <AcButton
                variant="ghost"
                disabled={busy}
                onClick={() => setView('digital_form')}
                data-testid="intake-back-to-edit"
              >
                Back to edit
              </AcButton>
            </div>
          </div>
        );
      case 'paper_pending':
        return (
          <div data-testid="intake-paper-pending" className="ac-wizard">
            <p>{INTAKE_PAPER_HANDED_COPY}</p>
            {/* C14 (B-PAPER): the signed paper artifact is NOT recorded until
                photographed -- the loud pending state carries exactly one
                action; capture/storage is the chartered PROOF-EVIDENCE-SPINE
                seam, so the slot below is honestly marked later work. */}
            {photoSlotOpen ? (
              <ConsentPhotoCaptureSlot
                seam={{ subject: 'intake_consent' }}
                onClose={() => setPhotoSlotOpen(false)}
              />
            ) : (
              <PendingPhotoBlock
                line={PENDING_PHOTO_LINE_PAPER_FORM}
                onTakePhoto={() => setPhotoSlotOpen(true)}
                disabled={busy}
                testId="intake-pending-photo"
              />
            )}
            <p className="intake-drawer__document-honesty">
              {INTAKE_PAPER_DOCUMENT_HONESTY_COPY}
            </p>
            <AcButton
              variant="primary"
              disabled={busy}
              onClick={() => setView('paper_review')}
              data-testid="intake-paper-returned"
            >
              {INTAKE_PAPER_RETURNED_COPY}
            </AcButton>
          </div>
        );
      case 'paper_review':
        return (
          <div data-testid="intake-paper-review" className="ac-wizard">
            <p>
              Type the returned form&apos;s details, correct anything unclear
              with the patient, then confirm.
            </p>
            {photoSlotOpen ? (
              <ConsentPhotoCaptureSlot
                seam={{ subject: 'intake_consent' }}
                onClose={() => setPhotoSlotOpen(false)}
              />
            ) : (
              <PendingPhotoBlock
                line={PENDING_PHOTO_LINE_PAPER_FORM}
                onTakePhoto={() => setPhotoSlotOpen(true)}
                disabled={busy}
                testId="intake-pending-photo"
              />
            )}
            <p className="intake-drawer__document-honesty">
              {INTAKE_PAPER_DOCUMENT_HONESTY_COPY}
            </p>
            {formFields(INTAKE_PAPER_CONFIRM_COPY, () =>
              validateThen(() => submitCompletion()),
            )}
          </div>
        );
      case 'identity_review':
        return (
          <div data-testid="intake-identity-review">
            <p>
              These existing records share the same number. Pick the matching
              patient, or create a new record if this is someone else (a shared
              family number is normal).
            </p>
            <ul className="intake-drawer__candidates">
              {(result?.candidates ?? []).map((c) => (
                <li key={c.patientId} className="intake-drawer__candidate">
                  <span className="intake-drawer__candidate-facts">
                    <strong>{c.fullName}</strong> <AcBadge tone="neutral">{c.umr}</AcBadge>
                    {c.ageYears !== null ? ` ${c.ageYears}y` : ''}
                    {c.gender ? ` ${c.gender}` : ''}
                    {c.city ? ` ${c.city}` : ''}
                  </span>
                  <AcButton
                    size="sm"
                    variant="primary"
                    disabled={busy}
                    onClick={() =>
                      submitCompletion({ action: 'link', patientId: c.patientId })
                    }
                    data-testid={`intake-candidate-link-${c.patientId}`}
                  >
                    {INTAKE_REVIEW_USE_COPY}
                  </AcButton>
                </li>
              ))}
            </ul>
            <AcButton
              variant="secondary"
              disabled={busy}
              onClick={() => submitCompletion({ action: 'create_new' })}
              data-testid="intake-candidate-create-new"
            >
              {INTAKE_REVIEW_CREATE_COPY}
            </AcButton>
          </div>
        );
      case 'identity_conflict':
        return (
          <div data-testid="intake-identity-conflict" role="alert">
            <h3 className="intake-drawer__done-title">
              {INTAKE_CONFLICT_TITLE_COPY}
            </h3>
            <p>{INTAKE_CONFLICT_BODY_COPY}</p>
            <FieldGrid
              columns={2}
              fields={[
                { label: 'Appointment', value: `#${appointmentId}` },
                {
                  label: 'Appointment link',
                  value: DIRECT_LINK_LABELS.conflict,
                  testId: 'intake-direct-link-state',
                },
                {
                  label: 'Verified patient (UMR)',
                  value: result?.patientUmr ?? null,
                },
                { label: 'Doctor queue', value: 'Not advanced' },
              ]}
              testId="intake-conflict-grid"
            />
            <AcButton
              variant="primary"
              onClick={onClose}
              data-testid="intake-conflict-close"
            >
              Close and review
            </AcButton>
          </div>
        );
      case 'done': {
        const checkedIn = result?.checkIn?.checkedIn === true;
        const directLink = result?.directAppointmentPatientLink ?? null;
        return (
          <div data-testid="intake-done">
            <h3 className="intake-drawer__done-title">
              {checkedIn ? INTAKE_DONE_TITLE_COPY : INTAKE_DONE_PARTIAL_TITLE_COPY}
            </h3>
            <FieldGrid
              columns={2}
              fields={[
                { label: 'Patient', value: form.fullName.trim() || patientLabel },
                { label: 'Patient ID (UMR)', value: result?.patientUmr ?? null },
                { label: 'Appointment', value: `#${appointmentId}` },
                {
                  // A-3 (founder corrective): the DIRECT appointment->patient
                  // reference is WALK-17-verifiable here, per scenario,
                  // without DB access.
                  label: 'Appointment link',
                  value: directLink !== null ? DIRECT_LINK_LABELS[directLink] : null,
                  missingLabel: 'Not recorded',
                  testId: 'intake-direct-link-state',
                },
                {
                  label: 'Doctor queue',
                  value: checkedIn ? 'In the queue' : 'Pending check-in',
                },
              ]}
              testId="intake-done-grid"
            />
            {result?.replayed ? (
              <p className="intake-drawer__notice">
                This intake was already recorded - nothing was submitted twice.
              </p>
            ) : null}
            {(result?.warnings ?? []).map((w) => (
              <p key={w} className="intake-drawer__warning" role="status">
                {w}
              </p>
            ))}
            {result?.checkIn?.warning ? (
              <p className="intake-drawer__warning" role="status">
                {result.checkIn.warning}
              </p>
            ) : null}
            <AcButton variant="primary" onClick={onClose} data-testid="intake-done-close">
              Done
            </AcButton>
          </div>
        );
      }
      default:
        return <></>;
    }
  };

  const showCancel =
    view !== 'choice' && view !== 'done' && view !== 'completed_review';

  return (
    <DrawerShell
      eyebrow="Patient intake"
      title={patientLabel}
      onClose={onClose}
      testId="intake-drawer"
      closeDisabled={busy}
      footer={
        /* AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3; W-6/W-7): the bypass
           ("Check in without intake") and benign-close ("Not now") footer
           doors are RETIRED with the choice panel -- close is the shell's
           X. "Cancel intake" (DESTRUCTIVE -- kills the session) survives:
           a wrong-person session still needs its lawful stop, and it is
           none of the four retired options. */
        <div className="intake-drawer__footer">
          {showCancel ? (
            <AcButton
              size="sm"
              variant="danger"
              disabled={busy}
              onClick={cancelSession}
              data-testid="intake-cancel"
            >
              {INTAKE_CANCEL_COPY}
            </AcButton>
          ) : null}
          <span className="intake-drawer__footer-spacer" aria-hidden="true" />
        </div>
      }
    >
      {serverError ? (
        <p className="intake-drawer__server-error" role="alert">
          {serverError}
        </p>
      ) : null}
      <div aria-busy={busy}>{body()}</div>
    </DrawerShell>
  );
}
